import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MagnifyingGlass, WhatsappLogo, CaretRight, MapPin } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Seo, SITE_URL } from "@/components/Seo";
import { Footer } from "@/components/Footer";
import { PropertyCard } from "@/components/PropertyCard";
import { SkeletonCard } from "@/components/Loaders";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useSiteInfo, waLink, track } from "@/lib/site";

const SLUG_TO_CAT = { farmhouses: "Farmhouses", resorts: "Resorts", "banquet-halls": "Banquet Halls", "function-halls": "Function Halls" };
const CAT_COPY = {
  Farmhouses: "private farmhouses with pools, lawns and BBQ — ideal for birthdays, day-outs and weekend getaways",
  Resorts: "resorts with pools, rooms and dining — perfect for family stays and celebrations",
  "Banquet Halls": "spacious banquet halls for weddings, receptions and grand functions",
  "Function Halls": "elegant function halls for every occasion and gathering",
};

const cap = (s) => (s || "").replace(/\b\w/g, (c) => c.toUpperCase());

export default function LandingPage({ category: fixedCat, categorySlug: fixedSlug, cityWide }) {
  const navigate = useNavigate();
  const params = useParams();
  const info = useSiteInfo();
  const category = fixedCat || SLUG_TO_CAT[params.categorySlug] || "Farmhouses";
  const slug = fixedSlug || params.categorySlug || Object.keys(SLUG_TO_CAT).find((k) => SLUG_TO_CAT[k] === category);
  const location = cityWide ? "" : cap((params.location || "").replace(/-/g, " "));
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [seo, setSeo] = useState({});

  const path = cityWide ? `/hyderabad-${slug}` : `/${slug}/${params.location}`;
  const title = location ? `${category} in ${location}, Hyderabad` : `${category} in Hyderabad`;
  const desc = `Book ${CAT_COPY[category]} ${location ? `in ${location}, Hyderabad` : "around Hyderabad"}. Compare prices, check availability and book online on Avinya Bookings.`;

  useEffect(() => {
    setItems(null);
    const p = new URLSearchParams({ category, city: "Hyderabad" });
    if (location) p.set("location", location);
    api.get(`/properties?${p.toString()}`).then((r) => setItems(r.data));
    track("category_view", { category, location });
  }, [category, location]);

  useEffect(() => { api.get(`/public/seo?path=${encodeURIComponent(path)}`).then((r) => setSeo(r.data || {})).catch(() => {}); }, [path]);

  const faqs = [
    { q: `How do I book a ${category.toLowerCase().replace(/s$/, "")} ${location ? `in ${location}` : "in Hyderabad"}?`, a: "Browse available properties below, pick your date, and submit a booking request. The owner confirms and you pay a small advance online to lock your booking." },
    { q: "Is there an advance payment?", a: "Yes — once the owner accepts your request, you pay a secure advance to confirm. The balance is payable at the venue." },
    { q: "Can I visit before booking?", a: "Many owners allow visits. Tap 'Chat on WhatsApp' and our team will help coordinate." },
  ];

  return (
    <>
      <Seo title={seo.title || title} description={seo.meta_description || desc} path={path} image={seo.image || undefined}
        jsonLd={{ "@context": "https://schema.org", "@type": "CollectionPage", name: seo.title || title, description: seo.meta_description || desc, url: `${SITE_URL}${path}` }} />

      <header className="border-b border-stone-200 bg-white/90 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between">
          <button onClick={() => navigate("/")} className="font-heading text-xl font-extrabold text-primary">Avinya <span className="font-semibold text-stone-800">Bookings</span></button>
          {info.support_whatsapp && (
            <a data-testid="landing-whatsapp" href={waLink(info.support_whatsapp, `Hi Avinya, I'm looking for ${category} ${location ? `in ${location}` : "in Hyderabad"}.`)} target="_blank" rel="noreferrer" onClick={() => track("whatsapp_click", { source: "landing" })} className="inline-flex items-center gap-2 rounded-full bg-green-500 px-4 py-2 text-sm font-semibold text-white hover:bg-green-600"><WhatsappLogo size={18} weight="fill" /> Chat on WhatsApp</a>
          )}
        </div>
      </header>

      <section className="bg-gradient-to-b from-accent/60 to-transparent px-4 py-12">
        <div className="mx-auto max-w-3xl text-center">
          {location && <span className="mb-2 inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-xs font-medium text-stone-600"><MapPin size={12} weight="fill" className="text-primary" /> {location}, Hyderabad</span>}
          <h1 className="font-heading text-3xl font-bold text-stone-900 md:text-5xl" data-testid="landing-h1">{seo.h1 || title}</h1>
          <p className="mx-auto mt-4 max-w-2xl text-base text-stone-600">{seo.intro || desc}</p>
          <form onSubmit={(e) => { e.preventDefault(); navigate(`/explore?q=${encodeURIComponent(q)}&category=${encodeURIComponent(category)}`); }} className="mx-auto mt-6 flex max-w-md items-center gap-2 rounded-full border border-stone-200 bg-white px-4 py-1.5">
            <MagnifyingGlass size={20} className="text-stone-400" />
            <Input data-testid="landing-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${category.toLowerCase()}…`} className="border-0 shadow-none focus-visible:ring-0" />
            <Button type="submit" className="rounded-full bg-primary px-5 hover:bg-green-900">Search</Button>
          </form>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 pb-28">
        <h2 className="font-heading text-xl font-semibold text-stone-900">Available {category} {location ? `in ${location}` : "in Hyderabad"}</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="landing-grid">
          {items === null ? [1, 2, 3].map((i) => <SkeletonCard key={i} />)
            : items.length === 0 ? <p className="text-sm text-stone-400">No properties listed here yet. Tap “Chat on WhatsApp” and we’ll help you find one.</p>
            : items.map((p) => <PropertyCard key={p.id} property={p} isFavorite={false} onToggleFavorite={() => navigate("/login")} />)}
        </div>

        <div className="mt-10 rounded-2xl border border-stone-200 bg-white p-6">
          <h2 className="font-heading text-xl font-semibold text-stone-900">Frequently asked questions</h2>
          <div className="mt-4 divide-y divide-stone-100">
            {(seo.faqs?.length ? seo.faqs : faqs).map((f, i) => (
              <div key={i} className="py-3">
                <p className="flex items-start gap-2 font-medium text-stone-800"><CaretRight size={16} className="mt-1 shrink-0 text-primary" /> {f.q}</p>
                <p className="mt-1 pl-6 text-sm text-stone-500">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
